import React, { useEffect, useState } from 'react';
import { IndividualExperience, ExperienceRecord, VerificationStatus } from '../types/experience';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { XIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
interface RecordDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  record: IndividualExperience | null;
  guarantor: ExperienceRecord | null;
  onVerify: (id: string) => void;
  onFlag: (id: string) => void;
  onSkip: (id: string) => void;
  onUpdateNotes: (id: string, notes: string) => void;
}
export function RecordDrawer({
  isOpen,
  onClose,
  record,
  guarantor,
  onVerify,
  onFlag,
  onSkip,
  onUpdateNotes
}: RecordDrawerProps) {
  const [notes, setNotes] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    if (record) {
      setNotes(record.triageNotes || '');
      setIsDirty(false);
    }
  }, [record]);
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setIsDirty(true);
  };
  const handleSaveNotes = () => {
    if (record) {
      onUpdateNotes(record.Id, notes);
      setIsDirty(false);
    }
  };
  if (!isOpen || !record || !guarantor) return null;
  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const RiskRow = ({
    label,
    value
  }: {
    label: string;
    value: boolean | null;
  }) => <div className="flex items-center justify-between py-3 px-4 border border-gray-200 rounded-lg bg-white" data-id="element-39">
      <span className="text-sm text-gray-600" data-id="element-40">{label}</span>
      {value === true ? <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-41">
          Yes
        </span> : value === false ? <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-42">
          No
        </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-400" data-id="element-43">
          —
        </span>}
    </div>;
  const VerifiedRow = ({
    label,
    value
  }: {
    label: string;
    value: boolean;
  }) => <div className="flex items-center justify-between py-3 px-4 border border-gray-200 rounded-lg bg-white" data-id="element-44">
      <span className="text-sm text-gray-600" data-id="element-45">{label}</span>
      {value ? <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-46">
          Yes
        </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-47">
          No
        </span>}
    </div>;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity" onClick={onClose} data-id="element-48" />

      {/* Drawer Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col" data-id="element-49">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 bg-white" data-id="element-50">
          <div className="flex items-start justify-between mb-1" data-id="element-51">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-52">
              Experience Details
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-53">
              <XIcon className="w-5 h-5" data-id="element-54" />
            </button>
          </div>
          <div className="text-sm text-amber-600 font-medium mb-4" data-id="element-55">
            {record.Name}
          </div>

          <div className="flex items-center space-x-2" data-id="element-56">
            <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal text-gray-600 border-gray-300" data-id="element-57">
              {record.Status__c || 'Pending'}
            </Badge>
            <span className="text-sm text-gray-600" data-id="element-58">
              {guarantor.Guarantor__c}
            </span>
          </div>
        </div>

        <div className="h-px bg-gray-100 w-full" data-id="element-59" />

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8" data-id="element-60">
          {/* Property Information */}
          <section data-id="element-61">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-62">
              Property Information
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-63">
              <div data-id="element-64">
                <div className="text-xs text-gray-500 mb-1" data-id="element-65">Address</div>
                <div className="font-bold text-gray-900 text-sm" data-id="element-66">
                  {record.Property_Address__c.street}
                </div>
                <div className="text-sm text-gray-500" data-id="element-67">
                  {record.Property_Address__c.city},{' '}
                  {record.Property_Address__c.zip}
                </div>
              </div>

              <div className="h-px bg-gray-200 w-full" data-id="element-68" />

              <div className="grid grid-cols-2 gap-4" data-id="element-69">
                <div data-id="element-70">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-71">Type</div>
                  <div className="text-sm text-gray-900" data-id="element-72">
                    {record.Experience_Type__c}
                  </div>
                </div>
                <div data-id="element-73">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-74">Units</div>
                  <div className="text-sm text-gray-900" data-id="element-75">
                    {record.Units__c || 1}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Financials */}
          <section data-id="element-76">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-77">
              Financials
            </h3>
            <div className="space-y-4" data-id="element-78">
              <div className="grid grid-cols-2 gap-4" data-id="element-79">
                <div data-id="element-80">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-81">
                    Purchase Price
                  </div>
                  <div className="font-bold text-gray-900 text-base" data-id="element-82">
                    {formatCurrency(record.Purchase_Price__c)}
                  </div>
                  <div className="text-xs text-gray-400 mt-1" data-id="element-83">
                    {formatDate(record.Purchase_Date__c)}
                  </div>
                </div>
                <div data-id="element-84">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-85">Sale Price</div>
                  <div className="font-bold text-gray-900 text-base" data-id="element-86">
                    {formatCurrency(record.Sale_Price__c)}
                  </div>
                  <div className="text-xs text-gray-400 mt-1" data-id="element-87">
                    {formatDate(record.Sale_Date__c)}
                  </div>
                </div>
              </div>

              <div className="h-px bg-gray-100 w-full" data-id="element-88" />

              <div className="grid grid-cols-2 gap-4" data-id="element-89">
                <div data-id="element-90">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-91">Rehab Amount</div>
                  <div className="text-sm text-gray-900" data-id="element-92">
                    {formatCurrency(record.Rehab_Amount__c)}
                  </div>
                </div>
                <div data-id="element-93">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-94">Net Profit</div>
                  <div className={`font-bold text-base ${(record.Net_Profit__c || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-95">
                    {formatCurrency(record.Net_Profit__c)}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Risk Indicators */}
          <section data-id="element-96">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-97">
              Risk Indicators
            </h3>
            <div className="space-y-2" data-id="element-98">
              <RiskRow label="Rehab Flag" value={record.Rehab_Flag__c} data-id="element-99" />
              <RiskRow label="Construction Flag" value={record.Construction_Flag__c} data-id="element-100" />
              <VerifiedRow label="Verified Flag" value={record.Verified_Flag__c} data-id="element-101" />
            </div>
          </section>

          {/* Triage Notes */}
          <section data-id="element-102">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-103">
              Triage Notes
            </h3>
            <textarea value={notes} onChange={handleNotesChange} onBlur={handleSaveNotes} placeholder="Add notes about this experience..." className="w-full h-32 p-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 text-sm resize-none placeholder-gray-400" data-id="element-104" />
          </section>
        </div>

        {/* Sticky Footer */}
        <div className="p-6 border-t border-gray-100 bg-white" data-id="element-105">
          <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-106">
            <button onClick={() => onVerify(record.Id)} className="flex items-center justify-center px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold transition-colors" data-id="element-107">
              <CheckCircleIcon className="w-5 h-5 mr-2" data-id="element-108" />
              Verify
            </button>
            <button onClick={() => onFlag(record.Id)} className="flex items-center justify-center px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-colors" data-id="element-109">
              <AlertTriangleIcon className="w-5 h-5 mr-2" data-id="element-110" />
              Flag
            </button>
          </div>
          <button onClick={() => onSkip(record.Id)} className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors text-sm" data-id="element-111">
            Skip for Now
          </button>
        </div>
      </div>
    </>;
}